// src/components/home/CategoryBanner.jsx
import React from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { PublicAPI } from '../../api/public';
import { normalizeMediaUrl } from '../../api/base';

// Импортируем fallback изображения
import rentOrSellImg from '../../assets/rentorsell.png';
import investImg from '../../assets/investement.png';
import lawImg from '../../assets/consultingandlaw.png';
import designImg from '../../assets/design.png';
import analyticsImg from '../../assets/analytics.png';
import noImageImg from '../../assets/no-image-640x400.jpg';

const Container = styled.section`
  margin: 32px 0;
  
  @media (max-width: 768px) {
    margin: 24px 0;
  }
`;

const Title = styled.h2`
  margin: 0 0 24px 0;
  font-size: clamp(28px, 5vw, 40px);
  font-weight: 800;
  text-align: center;
  color: #000;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  grid-template-rows: repeat(2, 1fr);
  gap: 16px;
  max-width: 1200px;
  margin: 0 auto;
  
  @media (max-width: 1024px) {
    gap: 12px;
  }
  
  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
    grid-template-rows: auto;
    gap: 10px;
  }
  
  @media (max-width: 480px) {
    grid-template-columns: 1fr;
    grid-template-rows: auto;
    gap: 8px;
  }
`;

const CategoryCard = styled(Link)`
  position: relative;
  aspect-ratio: 1;
  border-radius: 18px;
  overflow: hidden;
  border: 1px solid var(--bdr);
  box-shadow: var(--shadow);
  text-decoration: none;
  color: white;
  transition: all 0.3s ease;
  display: flex;
  flex-direction: column;
  
  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 12px 32px rgba(0, 0, 0, 0.15);
    
    .image {
      filter: grayscale(0%);
      transform: scale(1.05);
    }
  }
  
  &:active {
    transform: translateY(-2px);
  }
  
  @media (max-width: 768px) {
    border-radius: 14px;
  }
  
  @media (max-width: 480px) {
    aspect-ratio: 2.5;
    flex-direction: row;
    border-radius: 12px;
  }
`;

const Overlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(to right, rgba(236, 191, 121, 0.2), rgba(199, 146, 76, 0.2));
  z-index: 2;
  pointer-events: none;
`;

const CardContent = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  padding: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 3;
  
  .title {
    font-weight: 700;
    font-size: 16px;
    line-height: 1.25;
    display: flex;
    justify-content: center;
    align-items: center;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 2;
    overflow: hidden;
    height: calc(2.5em + 16px);
    flex: 1;
    min-width: 0;
    color: #000;
    background: var(--gold);
    opacity: 0.92;
    padding: 8px 12px;
    border-radius: 12px;
    backdrop-filter: blur(8px);
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.15);
    border: 1px solid rgba(255, 255, 255, 0.3);
    text-decoration: none;
    margin: 0;
    text-align: center;
  }
  
  @media (max-width: 480px) {
    padding: 12px;
    
    .title {
      font-size: 16px;
      text-align: center;
    }
  }
`;

const ImageContainer = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  overflow: hidden;
  z-index: 1;
  
  .image {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
    filter: grayscale(100%);
    transition: all 0.3s ease;
  }
`;

const LoadingState = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px;
  max-width: 1200px;
  margin: 0 auto;
  
  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`;

const LoadingCard = styled.div`
  aspect-ratio: 1;
  border-radius: 18px;
  background: var(--card);
  border: 1px solid var(--bdr);
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--muted);
  font-size: 14px;
`;

// Fallback изображения для услуг
const SERVICE_IMAGES = {
  'sell-or-rent': rentOrSellImg,
  'rent': rentOrSellImg,
  'sell': rentOrSellImg,
  'invest': investImg,
  'investment': investImg,
  'law': lawImg,
  'legal': lawImg,
  'consulting': lawImg,
  'design': designImg,
  'analytics': analyticsImg,
  'management': analyticsImg,
  'default': noImageImg
};

// Fallback изображения для категорий недвижимости
const CATEGORY_IMAGES = {
  'secondary': 'https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=400&h=300&fit=crop&crop=center',
  'rent': 'https://images.unsplash.com/photo-1522708323590-d24dbb6b0267?w=400&h=300&fit=crop&crop=center',
  'commercial': 'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=400&h=300&fit=crop&crop=center',
  'commercial-rent': 'https://images.unsplash.com/photo-1497366216548-37526070297c?w=400&h=300&fit=crop&crop=center',
  'mansions': 'https://images.unsplash.com/photo-1564013799919-ab600027ffc6?w=400&h=300&fit=crop&crop=center',
  'foreign': 'https://images.unsplash.com/photo-1449824913935-59a10b8d2000?w=400&h=300&fit=crop&crop=center',
  'suburban': 'https://images.unsplash.com/photo-1564013799919-ab600027ffc6?w=400&h=300&fit=crop&crop=center',
  'parking': 'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=400&h=300&fit=crop&crop=center'
};

// Функция для получения изображения
function getServiceImage(service) {
  // Отладочная информация
  
  // Приоритет 1: cover_url из API (правильный URL из медиа-библиотеки)
  if (service.cover_url) {
    const normalizedUrl = normalizeMediaUrl(service.cover_url);
    if (normalizedUrl && normalizedUrl !== 'null' && normalizedUrl !== 'undefined') {
      // Отладочная информация
      // Добавляем timestamp для обхода кеша изображений
      const separator = normalizedUrl.includes('?') ? '&' : '?';
      return `${normalizedUrl}${separator}_t=${Date.now()}`;
    }
  }
  
  // Приоритет 2: cover из API (для обратной совместимости)
  if (service.cover) {
    const normalizedUrl = normalizeMediaUrl(service.cover);
    if (normalizedUrl && normalizedUrl !== 'null' && normalizedUrl !== 'undefined') {
      // Отладочная информация
      // Добавляем timestamp для обхода кеша изображений
      const separator = normalizedUrl.includes('?') ? '&' : '?';
      return `${normalizedUrl}${separator}_t=${Date.now()}`;
    }
  }
  
  // Приоритет 3: fallback изображения (только если нет данных из API)
  const serviceType = service.slug || service.type || service.link?.split('/').pop() || service.icon || '';
  const fallbackImage = SERVICE_IMAGES[serviceType.toLowerCase()] || 
                       CATEGORY_IMAGES[serviceType.toLowerCase()] ||
                       SERVICE_IMAGES.default;
  
  // Отладочная информация
  return fallbackImage;
}

// Функция для получения правильного заголовка
function getServiceTitle(service, language) {
  if (language === 'zh') {
    return service.title_zh || service.title_en || service.title_ru || service.title || '服务';
  } else if (language === 'en') {
    return service.title_en || service.title_ru || service.title || 'Service';
  } else {
    return service.title_ru || service.title_en || service.title || 'Услуга';
  }
}

export default function CategoryBanner() {
  const { t, i18n } = useTranslation();
  const [services, setServices] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [refreshKey, setRefreshKey] = React.useState(0);

  // Функция для принудительного обновления
  const refreshServices = React.useCallback(() => {
    setRefreshKey(prev => prev + 1);
  }, []);

  // Слушаем события обновления услуг
  React.useEffect(() => {
    const handleServicesUpdate = () => {
      refreshServices();
    };

    // Слушаем кастомное событие обновления услуг
    window.addEventListener('servicesUpdated', handleServicesUpdate);
    
    return () => {
      window.removeEventListener('servicesUpdated', handleServicesUpdate);
    };
  }, [refreshServices]);

  // Загрузка услуг из API
  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const res = await PublicAPI.services({
          locale: i18n.language || 'ru',
          // Добавляем timestamp для обхода кеша
          _t: Date.now()
        });
        // Отладочная информация
        // Синхронизируем с Services.jsx - проверяем все возможные варианты ответа
        const list = Array.isArray(res?.services) ? res.services : 
                     Array.isArray(res?.items) ? res.items : 
                     Array.isArray(res) ? res : [];
        
        // Фильтруем только активные услуги и ограничиваем до 8 для сетки
        const activeServices = list
          .filter(service => service.is_active !== false)
          .slice(0, 8);
        
        // Отладочная информация
        
        if (!cancelled) {
          setServices(activeServices);
        }
      } catch (error) {
        console.error('Failed to load services for CategoryBanner:', error);
        if (!cancelled) {
          setServices([]);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [i18n.language, refreshKey]); // Добавляем refreshKey в зависимости

  if (loading) {
    return (
      <Container>
        <Title>{t('categoryBanner.title', 'Наши услуги и направления')}</Title>
        <LoadingState>
          {Array.from({ length: 8 }).map((_, index) => (
            <LoadingCard key={index}>
              {t('common.loading', 'Загрузка...')}
            </LoadingCard>
          ))}
        </LoadingState>
      </Container>
    );
  }

  if (!services.length) {
    return (
      <Container>
        <Title>{t('categoryBanner.title', 'Наши услуги и направления')}</Title>
        <LoadingState>
          <LoadingCard style={{ gridColumn: '1 / -1', textAlign: 'center' }}>
            {t('services.noServices', 'Услуги временно недоступны')}
          </LoadingCard>
        </LoadingState>
      </Container>
    );
  }

  return (
    <Container>
      <Title>{t('categoryBanner.title', 'Наши услуги и направления')}</Title>
      
      <Grid>
        {services.map((service) => {
          const title = getServiceTitle(service, i18n.language);
          const image = getServiceImage(service);
          const link = service.link || (service.slug ? `/services/${service.slug}` : '#');
          
          return (
            <CategoryCard
              key={service.id}
              to={link}
            >
              <CardContent>
                <h3 className="title">{title}</h3>
              </CardContent>
              <ImageContainer>
                <img 
                  className="image" 
                  src={image} 
                  alt={title}
                  loading="lazy"
                  decoding="async"
                />
              </ImageContainer>
              <Overlay />
            </CategoryCard>
          );
        })}
      </Grid>
    </Container>
  );
}